import type { FilterChangeMeta, FilterValues, FilterBarPropsEvents } from '../types';
import type { State } from './reducer';
import { currentValues } from './reducer';

/**
 * Init sequencer — implements the runtime invariants:
 *
 *   1. **Init silence** — `onChange` never fires between mount and `onInit`.
 *   2. **Transactional cascade** — Exactly one `onChange` per closed transaction.
 *
 * The reducer increments `closedTxnCount` on every CASCADE_CLOSE (and `initEmitCount`
 * on SET_INITIALIZED). The sequencer compares prev/next counters across renders.
 * Counter-based detection survives React 18's automatic batching — multiple
 * dispatches in one tick collapse to a single render, but the counter still ticks.
 */
export interface EmissionContext<T extends FilterValues = FilterValues>
  extends FilterBarPropsEvents<T> {
  initializedRef: { current: boolean };
}

export interface EmissionResult {
  emittedInit: boolean;
  emittedChange: boolean;
}

export function emitIfReady<T extends FilterValues = FilterValues>(
  prev: State,
  next: State,
  ctx: EmissionContext<T>,
): EmissionResult {
  const result: EmissionResult = { emittedInit: false, emittedChange: false };

  if (prev.initEmitCount !== next.initEmitCount) {
    ctx.initializedRef.current = true;
    const values = currentValues(next) as T;
    ctx.onInit?.(values);
    result.emittedInit = true;
  }

  if (prev.closedTxnCount !== next.closedTxnCount) {
    if (!ctx.initializedRef.current) return result;

    const values = currentValues(next) as T;
    const prevValues = next.prevValuesAtTxnStart;
    const affected = computeAffected(prevValues, values);
    const hasErrors = (next.partialErrors?.length ?? 0) > 0;
    if (affected.length === 0 && !hasErrors) {
      return result;
    }
    const meta: FilterChangeMeta = {
      trigger: next.transactionTrigger,
      operation: next.transactionOperation,
      affected,
      prevValues,
      source: next.transactionSource,
      partialErrors: hasErrors ? [...next.partialErrors] : undefined,
    };

    if (ctx.onFieldChange) {
      for (const name of next.itemsOrder) {
        if (!affected.includes(name)) continue;
        const handler = ctx.onFieldChange[name];
        if (handler) handler(values[name], meta);
      }
    }
    ctx.onChange?.(values, meta);
    result.emittedChange = true;
  }

  return result;
}

function computeAffected(prev: FilterValues, next: FilterValues): string[] {
  const names = new Set<string>([...Object.keys(prev), ...Object.keys(next)]);
  const out: string[] = [];
  for (const name of names) {
    if (!shallowEqualValue(prev[name], next[name])) out.push(name);
  }
  return out;
}

/**
 * One-level-deep equality for value comparison.
 * - Arrays: length check + element-wise Object.is (covers `[42]` rebuilt each render).
 * - Otherwise: Object.is.
 *
 * Plan Section "Controlled vs uncontrolled" — equality semantics for the prop-sync diff.
 */
export function shallowEqualValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!Object.is(a[i], b[i])) return false;
    }
    return true;
  }
  return false;
}

export function shallowEqualValues(a: FilterValues, b: FilterValues): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!(k in b)) return false;
    if (!shallowEqualValue(a[k], b[k])) return false;
  }
  return true;
}
